// Internal API calls go through the backend proxy at /api/internal/call.
// The response interceptor owns app-wide auth-expiry handling.
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../stores/auth.js';

import { API_BASE_URL } from '../utils/api-base.js';

const http = axios.create({ baseURL: API_BASE_URL, timeout: 15000 });

http.interceptors.request.use((config) => {
	const { token, user } = useAuthStore.getState();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.account) config.headers['X-Ulucu-Account'] = user.account;
	return config;
});

const AUTH_FAIL_CODES = new Set([401, 100100, 100200, 100201, 100300]);
const AUTH_FAIL_MSG_PATTERNS = [
	/token/i,
	/invalid\s*token/i,
	/logged\s*in\s*elsewhere/i,
	/login\s*elsewhere/i,
	/elsewhere/i,
	/re-?login/i,
	/未登录/,
	/登录失效/,
	/异地登录/,
	/别处登录/,
	/其他.*登录/,
	/not\s*log/i,
	/unauthor/i,
];
const AUTH_LOGOUT_REASON_KEY = 'ulucu.auth.logoutReason';

function readInternalRequest(data: unknown): Partial<InternalCallParams> | null {
	if (!data) return null;
	if (typeof data === 'string') {
		try {
			const parsed = JSON.parse(data) as unknown;
			return parsed && typeof parsed === 'object' ? parsed as Partial<InternalCallParams> : null;
		} catch {
			return null;
		}
	}
	if (typeof data === 'object') return data as Partial<InternalCallParams>;
	return null;
}

function readResponseCode(payload: unknown): number | null {
	if (!payload || typeof payload !== 'object') return null;
	const code = (payload as { code?: unknown }).code;
	if (code == null || code === '') return null;
	const parsed = Number(code);
	return Number.isFinite(parsed) ? parsed : null;
}

function readResponseMessage(payload: unknown): string {
	if (!payload || typeof payload !== 'object') return '';
	const obj = payload as { msg?: unknown; message?: unknown; error?: unknown };
	return [obj.msg, obj.message, obj.error]
		.filter((item): item is string => typeof item === 'string' && item.length > 0)
		.join(' ');
}

function isModuleScopedTokenError(payload: unknown, config?: InternalAxiosRequestConfig): boolean {
	if (readResponseCode(payload) !== 701006) return false;
	const request = readInternalRequest(config?.data);
	if (request?.short_name === 'kefu') return true;
	if (request?.short_name === 'base' && (
		request.action === '/user/edit_self'
		|| request.action === '/other/upai_config'
	)) return true;
	return false;
}

function isAuthFailure(status: number, payload: unknown, config?: InternalAxiosRequestConfig): boolean {
	if (status === 401 || status === 403) return true;
	if (!payload || typeof payload !== 'object') return false;
	if (isModuleScopedTokenError(payload, config)) return false;
	const code = readResponseCode(payload);
	if (code === 701006) return true;
	if (code != null && AUTH_FAIL_CODES.has(code)) return true;
	const msg = readResponseMessage(payload);
	if (code == null && msg && AUTH_FAIL_MSG_PATTERNS.some((re) => re.test(msg))) return true;
	return false;
}

function handleAuthFailure() {
	const { token } = useAuthStore.getState();
	if (!token) return;
	useAuthStore.getState().clearAuth();
	try {
		sessionStorage.setItem(AUTH_LOGOUT_REASON_KEY, 'elsewhere');
	} catch {
		// Ignore private mode / storage quota failures.
	}
	if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
		const next = encodeURIComponent(window.location.pathname + window.location.search);
		window.location.href = `/login?next=${next}&reason=elsewhere`;
	}
}

http.interceptors.response.use(
	(res) => {
		if (isAuthFailure(res.status, res.data, res.config)) {
			handleAuthFailure();
		}
		return res;
	},
	(err: AxiosError) => {
		if (err.response && isAuthFailure(err.response.status, err.response.data, err.response.config)) {
			handleAuthFailure();
		}
		return Promise.reject(err);
	},
);

export interface InternalResponse<T = unknown> {
	code: number;
	msg: string;
	data?: T;
	data_md5?: string;
}

export interface InternalCallParams {
	short_name: string;
	action: string;
	method?: 'GET' | 'POST';
	query?: Record<string, string | number>;
	body?: Record<string, unknown>;
	av?: string;
}

export async function callInternal<T = unknown>(params: InternalCallParams): Promise<InternalResponse<T>> {
	const { data } = await http.post<InternalResponse<T>>('/internal/call', params);
	return data;
}
